import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import { ArrowRight, Check, Eye, EyeOff, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AuthShell } from "@/components/auth/AuthShell";

const rules = (pwd: string) => [
  { ok: pwd.length >= 8, label: "8+ characters" },
  { ok: /[A-Z]/.test(pwd), label: "Uppercase letter" },
  { ok: /[a-z]/.test(pwd), label: "Lowercase letter" },
  { ok: /[0-9]/.test(pwd), label: "Number" },
];

const ResetPassword = () => {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [valid, setValid] = useState(false);
  const [checking, setChecking] = useState(true);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    let unsub: (() => void) | undefined;
    (async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        setValid(true);
        setChecking(false);
        return;
      }
      const { data: { subscription } } = supabase.auth.onAuthStateChange((event, s) => {
        if (event === "PASSWORD_RECOVERY" && s) {
          setValid(true);
          setChecking(false);
        }
      });
      unsub = () => subscription.unsubscribe();
      setTimeout(() => setChecking(false), 2000);
    })();
    return () => unsub?.();
  }, []);

  const checks = rules(password);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (checks.some((r) => !r.ok)) {
      toast.error("Password doesn't meet requirements");
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords don't match");
      return;
    }
    setLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      setSuccess(true);
      toast.success("Password updated");
      setTimeout(() => navigate("/dashboard"), 1500);
    } catch (err: any) {
      toast.error("Couldn't update password", { description: err.message });
    } finally {
      setLoading(false);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background text-muted-foreground text-sm">
        Verifying reset link…
      </div>
    );
  }

  if (!valid) {
    return (
      <>
        <Helmet><title>Invalid reset link | EchoCRM</title><meta name="robots" content="noindex, nofollow" /></Helmet>
        <AuthShell
          eyebrow="Reset password"
          title="This link is invalid or expired."
          subtitle="Request a new reset link to continue."
        >
          <div className="flex flex-col gap-3">
            <Button size="lg" className="w-full" onClick={() => navigate("/forgot-password")}>
              Request new link <ArrowRight className="h-4 w-4" />
            </Button>
            <Button size="lg" variant="outline" className="w-full" onClick={() => navigate("/login")}>
              Back to sign in
            </Button>
          </div>
        </AuthShell>
      </>
    );
  }

  return (
    <>
      <Helmet><title>Set a new password | EchoCRM</title><meta name="robots" content="noindex, nofollow" /></Helmet>
      <AuthShell
        eyebrow={success ? "All set" : "New password"}
        title={success ? "Password updated." : "Set a new password."}
        subtitle={success ? "Redirecting you to your workspace…" : "Choose something strong you'll remember."}
      >
        {!success && (
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-[13px] font-medium">New password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPwd ? "text" : "password"}
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  aria-label={showPwd ? "Hide password" : "Show password"}
                >
                  {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {password && (
                <ul className="grid grid-cols-2 gap-x-4 gap-y-1 mt-2">
                  {checks.map((r) => (
                    <li key={r.label} className={`flex items-center gap-1.5 text-[12px] ${r.ok ? "text-foreground" : "text-muted-foreground"}`}>
                      {r.ok ? <Check className="h-3 w-3 text-primary" /> : <X className="h-3 w-3 opacity-50" />}
                      {r.label}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="confirm" className="text-[13px] font-medium">Confirm password</Label>
              <Input
                id="confirm"
                type={showPwd ? "text" : "password"}
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
              />
            </div>

            <Button type="submit" size="lg" className="w-full" disabled={loading}>
              {loading ? "Updating…" : <>Update password <ArrowRight className="h-4 w-4" /></>}
            </Button>
          </form>
        )}
      </AuthShell>
    </>
  );
};

export default ResetPassword;